import type {FeatureStates} from '@github-ui/pull-requests/page-data/payloads/header'
import type {JSONAPIPullRequestPayload, PullRequestMergeRequirementsPayload} from '../../page-data/payloads/merge-box'
import type {StackMergeabilityPageData} from '../../page-data/payloads/stack-mergeability'
import type {StatusChecksPageData} from '../../page-data/payloads/status-checks'
import {BlockedSectionStatus} from './blocked-section-status'
import {ChecksSectionStatus} from './checks-section-status'
import {ClosedOrMergedStateSectionStatus} from './closed-or-merged-state-section-status'
import {ConflictsSectionStatus} from './conflicts-section-status'
import {DraftStateSectionStatus} from './draft-state-section-status'
import {MergeQueueSectionStatus} from './merge-queue-section-status'
import {ReviewerSectionStatus} from './reviewer-section-status'
import {StackSectionStatus} from './stack-section-status'
import type {MergeBoxRollupStatus} from './types'
import type {UseQueryResult} from '@github-ui/react-query'
import {StacksMergeConflictsAndRebaseSectionStatus} from './stacks-merge-conflicts-and-rebase-section-status'
import type {PullRequestMergeAction} from '../../types'
import {MergeSectionStatus} from './merge-section-status'

type Sections = {
  BlockedSection: BlockedSectionStatus
  ChecksSection: ChecksSectionStatus
  ClosedOrMergedStateMergeBox: ClosedOrMergedStateSectionStatus
  ConflictsSection: ConflictsSectionStatus
  DraftStateSection: DraftStateSectionStatus
  MergeQueueSection: MergeQueueSectionStatus
  MergeSection: MergeSectionStatus
  ReviewerSection: ReviewerSectionStatus
  StackSection: StackSectionStatus
  StacksMergeConflictsAndRebaseSection: StacksMergeConflictsAndRebaseSectionStatus
}

/**
 * Derives the status of the merge box based on the status of each section
 *
 * Each section corresponds to an immediate child of the `MergeBox` component
 * The status will be used to determine the color of the border, the icon color of each section, and the color of the merge box button
 */
export class MergeBoxStatusCalculator {
  #pullRequest: JSONAPIPullRequestPayload
  #mergeRequirements: PullRequestMergeRequirementsPayload | null
  #statusChecks: StatusChecksPageData | undefined
  #sectionsInternal: Sections
  #featureStates: FeatureStates
  #stackMergeabilityQueries: Array<UseQueryResult<StackMergeabilityPageData>> | undefined
  #isRebasing: boolean
  #pendingStackMergeAction: PullRequestMergeAction | undefined
  #mergeBoxDataUpdatedAt: number

  constructor(
    pullRequest: JSONAPIPullRequestPayload,
    mergeRequirements: PullRequestMergeRequirementsPayload | null,
    statusChecks: StatusChecksPageData | undefined,
    featureStates: FeatureStates,
    stackMergeabilityQueries: Array<UseQueryResult<StackMergeabilityPageData>> | undefined = undefined,
    isRebasing = false,
    pendingStackMergeAction?: PullRequestMergeAction,
    mergeBoxDataUpdatedAt = 0,
  ) {
    this.#pullRequest = pullRequest
    this.#mergeRequirements = mergeRequirements
    this.#statusChecks = statusChecks
    this.#featureStates = featureStates
    this.#stackMergeabilityQueries = stackMergeabilityQueries
    this.#isRebasing = isRebasing
    this.#pendingStackMergeAction = pendingStackMergeAction
    this.#mergeBoxDataUpdatedAt = mergeBoxDataUpdatedAt
    this.#sectionsInternal = this.#setSections()
  }

  get overallStatus(): MergeBoxRollupStatus {
    if (this.#pullRequest.state === 'MERGED') return 'MERGED'
    if (this.#pullRequest.state === 'CLOSED') return 'NEUTRAL'
    if (this.#pullRequest.isInMergeQueue) return 'QUEUED'
    if (this.#pullRequest.isDraft) return 'NEUTRAL'

    // Only consider the sections that will be rendered
    const relevantStatuses = Object.values(this.sections ?? []).filter(section => section.shouldConsiderStatus)

    const anyFailed = relevantStatuses.some(section => section.mergeBoxStatus === 'FAILED')
    const allPassed = relevantStatuses.every(section => section.mergeBoxStatus === 'PASSED')

    if (anyFailed && this.#pullRequest.viewerCanUpdate) {
      return 'SOME_FAILED'
    } else if (allPassed) {
      return 'ALL_PASSED'
    } else {
      return 'NEUTRAL'
    }
  }

  get sections() {
    return this.#sectionsInternal
  }

  #setSections(): Sections {
    const stackSection = new StackSectionStatus(
      this.#pullRequest,
      this.#mergeRequirements,
      this.#statusChecks,
      this.#featureStates,
      this.#stackMergeabilityQueries,
      this.#isRebasing,
      this.#pendingStackMergeAction,
      this.#mergeBoxDataUpdatedAt,
    )

    return {
      BlockedSection: new BlockedSectionStatus(
        this.#pullRequest,
        this.#mergeRequirements,
        this.#statusChecks,
        this.#featureStates,
        this.#stackMergeabilityQueries,
      ),
      ClosedOrMergedStateMergeBox: new ClosedOrMergedStateSectionStatus(
        this.#pullRequest,
        this.#mergeRequirements,
        this.#statusChecks,
        this.#featureStates,
        this.#stackMergeabilityQueries,
      ),
      ChecksSection: new ChecksSectionStatus(
        this.#pullRequest,
        this.#mergeRequirements,
        this.#statusChecks,
        this.#featureStates,
        this.#stackMergeabilityQueries,
      ),
      ConflictsSection: new ConflictsSectionStatus(
        this.#pullRequest,
        this.#mergeRequirements,
        this.#statusChecks,
        this.#featureStates,
        this.#stackMergeabilityQueries,
      ),
      DraftStateSection: new DraftStateSectionStatus(
        this.#pullRequest,
        this.#mergeRequirements,
        this.#statusChecks,
        this.#featureStates,
        this.#stackMergeabilityQueries,
      ),
      MergeQueueSection: new MergeQueueSectionStatus(
        this.#pullRequest,
        this.#mergeRequirements,
        this.#statusChecks,
        this.#featureStates,
        this.#stackMergeabilityQueries,
      ),
      MergeSection: new MergeSectionStatus(
        this.#pullRequest,
        this.#mergeRequirements,
        this.#statusChecks,
        this.#featureStates,
        this.#stackMergeabilityQueries,
        stackSection,
      ),
      ReviewerSection: new ReviewerSectionStatus(
        this.#pullRequest,
        this.#mergeRequirements,
        this.#statusChecks,
        this.#featureStates,
        this.#stackMergeabilityQueries,
      ),
      StackSection: stackSection,
      StacksMergeConflictsAndRebaseSection: new StacksMergeConflictsAndRebaseSectionStatus(
        this.#pullRequest,
        this.#mergeRequirements,
      ),
    }
  }
}
